import type { Ref } from "react";
import { Button } from "../atoms/index.js";
import { Tooltip } from "../molecules/index.js";
import type { PlayerAITabId } from "./playerai.types";

export interface PlayerAILauncherProps {
  /** Called when the user activates the floating launcher. */
  onOpenPane: (tab: PlayerAITabId) => void;
  /** When true, PlayerAI is open and the mounted launcher is hidden. */
  paneOpen?: boolean;
  /** Optional override for the visible/aria label when expanded. */
  label?: string;
  /** Optional aria-label override for the close state. */
  closeLabel?: string;
  /** Host-owned ref used to restore focus after a PlayerAI surface closes. */
  buttonRef?: Ref<HTMLButtonElement>;
}

/**
 * Floating bottom-right launcher for Player AI. The text label keeps the
 * assistant entry point explicit without relying on icon recognition.
 *
 * The launcher remains mounted but becomes hidden and non-interactive while
 * any PlayerAI presentation is open.
 */
export function PlayerAILauncher({
  onOpenPane,
  paneOpen = false,
  label = "Ask Player AI",
  closeLabel = "Close Player AI",
  buttonRef,
}: PlayerAILauncherProps) {
  // Keep the element mounted so transitions animate smoothly, but make it
  // invisible and non-interactive while any PlayerAI surface is open — each
  // presentation owns its close affordance.
  const hidden = paneOpen;
  const ariaLabel = paneOpen ? closeLabel : label;
  return (
    <div
      aria-hidden={hidden || undefined}
      // The host: it carries the fixed placement, the fade and `aria-hidden`.
      // The Tooltip's measuring span sits between it and the button, so a
      // reader (the geometry gate, the shell tests) finds it by this handle,
      // never as the button's `parentElement`.
      data-playerai-launcher-host
      // At sm+, track the rail width so the launcher parks beside an open module
      // rail, falling back to the 2rem viewport offset when none is open.
      //
      // `z-50`, one layer above a canvas-bounded expanded card (`z-40`). The
      // VSM centered editor is the exception: its Save action occupies the
      // same corner, so the launcher steps aside until the editor closes.
      // A full-viewport overlay (`z-50`, later in the DOM) still covers it.
      //
      // Below sm it hides instead. Keeping it at `end-8` there left it painting
      // over the rail — it is `fixed` against an in-flow rail, so it wins
      // the stacking order and covers the rail's own controls. Offsetting is not
      // an option either: at 375px a 320px rail plus a ~146px launcher do not
      // both fit, so the offset would push it off-screen. With no room for both,
      // the rail owns the edge and the launcher steps aside.
      //
      // CSS rather than state: the rail already publishes to the document root,
      // and the geometry gate (`playerai-safe-area.mjs`) is what proves the
      // non-overlap in a real browser — jsdom has no layout engine to prove it.
      className={`fixed z-50 bottom-8 end-8 [html:has(.vsm-centered-editor)_&]:hidden max-sm:[html[data-pb-rail-open]_&]:hidden sm:end-[calc(var(--pb-workspace-rail-width,0px)+2rem)] transition-[opacity,transform] duration-motion-layout ease-motion-standard${
        hidden
          ? " pointer-events-none opacity-0 scale-95"
          : " scale-100 opacity-100"
      }`}
    >
      {/* The iris CTA (ADR-0076): Button primary + ai, pill, 44px (OK-03).
          The label is visible, so the tooltip does not describe the button a
          second time. */}
      <Tooltip content={ariaLabel} describeChild={false}>
        <Button
          ref={buttonRef}
          variant="primary"
          tone="ai"
          shape="pill"
          size="lg"
          onClick={() => onOpenPane("chat")}
          aria-label={ariaLabel}
          aria-pressed={paneOpen}
          tabIndex={hidden ? -1 : undefined}
          data-playerai-launcher
          /* Stable semantic handle for the onboarding tour. Separate from
             `data-playerai-launcher`, which the geometry gate owns — one
             attribute serving two unrelated consumers is how a change for one
             of them quietly breaks the other. */
          data-tour-id="shell.player-ai"
          className="whitespace-nowrap"
        >
          {label}
        </Button>
      </Tooltip>
    </div>
  );
}
